/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import React, { useState } from 'react';
import _ from 'lodash';
import { subsidyUsed } from 'hooks/round3/subsidy';
import cn from 'classnames';
import { callAPI, notApplicable, getProjectScore, getFullScore, openPdfTab, userErrorMessage, showErrorToast, formatThaiShortDate, thaiMoment } from 'helpers';
import { PREV_TCAS_LABEL } from 'constants/school-year';
import enabled from 'data/enabled.json';
import { LATE_FEE, LATE_PAY_FIRST, admissionLateFee, isLatePendingAdmission, isExpiredPendingAdmission, replacesThirdConfirmation, round3QuotaUsed } from 'late-fee';
import { Link } from 'react-router-dom';
import PassChance from './PassChance';

// Hooks
import { useSelector } from 'react-redux';
import useIsMobile from 'hooks/useIsMobile';

const statusMap = {
  pending: 'รอการชำระเงิน',
  paid: 'ชำระเงินเรียบร้อยแล้ว',
  canceled: 'ยกเลิกใบแจ้งชำระเงิน',
};
const prices = [150, 200, 250, 300, 400, 500, 600, 700, 800, 900];

const AdmissionApplied = ({ studentStatus, admissions, offerProjects, onEdit, alreadySetZero, redeemableAmount, redeemedAmount, isExpired, phase }) => {
  const profile = useSelector((state) => _.get(state, 'app.user') || {});
  const courseMap = useSelector((state) => _.get(state, 'app.courseMap')) || {};
  const admissionCountMap = useSelector((state) => _.get(state, 'app.admissionCountMap')) || {};
  const skipRedemption = _.get(profile, 'skip_r3_redemption');

  const [sectionActive, setSectionActive] = useState(true);
  const [editActive, setEditActive] = useState(false);
  const [errorActive, setErrorActive] = useState({});
  const isMobile = useIsMobile();

  const paidAdmissions = admissions.filter(({ status, rd_rollback, sz_rollback }) => status === 'paid' || rd_rollback || sz_rollback);
  const paidAmount = _.sumBy(paidAdmissions, 'payment_amount') + subsidyUsed({ alreadySetZero, skipRedemption, redeemableAmount, redeemedAmount });
  //  + _.sumBy(paidAdmissions, 'redeem_amount');
  // const paidSubjectCount = (_.get(_.maxBy(paidAdmissions, 'total_amount'), 'program_projects') || []).length;
  const paidSubjectCount = prices.indexOf(paidAmount) + 1;

  let recentAdmission = _.last(isExpired ? paidAdmissions : admissions) || {};
  const recentPaidAdmission = _.last(paidAdmissions) || {};
  const quotaUsed = round3QuotaUsed(admissions, phase);

  if (isExpired) {
    recentAdmission.program_projects = offerProjects;
  }

  // สมัครล่าช้า (ทปอ. 17 ต.ค. 69): ใบล่าช้า = payment_amount + late_fee 200 · ใบช่วงปกติค้าง = หมดอายุ ยืนยันใหม่ได้ (ชุดเดิมก็ได้)
  // · ใบล่าช้าค้าง = ต้องจ่ายก่อนยืนยันเพิ่ม (pay-first) — src/late-fee.js
  const lateFee = admissionLateFee(recentAdmission);
  const expiredInvoice = !isExpired && isExpiredPendingAdmission(recentAdmission, phase);
  const latePending = isLatePendingAdmission(recentAdmission);
  const owedLateFee = latePending ? lateFee : 0;
  // ใบช่วงปกติชำระภายในวันสุดท้ายของช่วงปกติ · ใบล่าช้าภายในวันสุดท้ายของช่วงล่าช้า
  const paymentDeadline = lateFee ? enabled.round3.apply_end : thaiMoment(enabled.round3.late_start).subtract(1, 'ms');

  const [invoiceOpening, setInvoiceOpening] = useState(false);
  const onInvoicePdfOpen = async () => {
    if (invoiceOpening) return;
    const pdfTab = openPdfTab();   // ต้องเปิดแบบ sync ก่อน await — ดูเหตุผล bfcache/presign ใน helpers
    try {
      setInvoiceOpening(true);
      const { pdf } = await callAPI({
        genPdf: true,
        method: 'POST',
        url: '/apply-round3/invoice-pdf'
        // url: '/applicants/me/apply-round3-invoice-pdf',
      });

      pdfTab.navigate(pdf);

      setInvoiceOpening(false);
    } catch (error) {
      pdfTab.close();
      console.error(`Error while trying to download invoice pdf: ${error.message}`);
      showErrorToast(userErrorMessage(error));
      setInvoiceOpening(false);
    }
  };

  const [summaryOpening, setSummaryOpening] = useState(false);
  const onSummaryPdfOpen = async () => {
    if (summaryOpening) return;
    const pdfTab = openPdfTab();
    try {
      setSummaryOpening(true);
      const { pdf } = await callAPI({
        genPdf: true,
        method: 'POST',
        url: '/apply-round3/summary-pdf'
        // url: '/applicants/me/apply-round3-summary-pdf',
      });

      pdfTab.navigate(pdf);

      setSummaryOpening(false);
    } catch (error) {
      pdfTab.close();
      console.error(`Error while trying to download summary pdf: ${error.message}`);
      showErrorToast(userErrorMessage(error));
      setSummaryOpening(false);
    }
  };

  if (!recentAdmission._id) {
    return (
      <div className={cn('t-collapse', { active: sectionActive })}>
        <div className='t-content'>
          <h3>{'ท่านไม่ได้สมัครในรอบนี้'}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('t-collapse', { active: sectionActive })}>
      <h3 className='r-3-bar t-toggle' onClick={() => setSectionActive((_active) => !_active)}>
        <i className='i-apply'></i>
        {'สาขาวิชาที่สมัคร'}
        {(recentAdmission.program_projects || []).length ? ` (${(recentAdmission.program_projects || []).length}/10)` : ''}
        <i className='i-toggle i-down-teal' />
      </h3>
      <div className='t-content'>
        <h4>
          <b>
            {'การสมัครครั้งที่'} {recentAdmission.apply_id} / {'ยืนยันครั้งที่'} {recentAdmission.confirm_id}
          </b>
        </h4>
        {recentAdmission.status === 'paid' ? (
          <p>
            <b className='color-green'>{'ชำระเงินเรียบร้อยแล้ว'}</b>
          </p>
        ) : expiredInvoice ? (
          <p data-testid='r3-expired-invoice'>
            <b className='color-orange'>{'ใบแจ้งชำระเงินจากช่วงรับสมัครปกติหมดอายุแล้ว กรุณายืนยันการสมัครใหม่'}</b>
            <br />
            {`(เลือกสาขาวิชาชุดเดิมได้) ใบแจ้งชำระเงินใหม่จะรวมค่าธรรมเนียมสมัครล่าช้า ${LATE_FEE} บาท`}
          </p>
        ) : recentAdmission.status === 'pending' ? (
          <p>
            {'ท่านยืนยันการสมัครแล้ว'} <b className='color-red'>{'รอการชำระเงิน'}</b>
            {latePending && (
              <>
                <br />
                <b className='color-orange' data-testid='r3-late-pay-first'>{LATE_PAY_FIRST}</b>
              </>
            )}
          </p>
        ) : recentAdmission.status === 'canceled' ? (
          // ใบแจ้งชำระถูกยกเลิก (เช่น เจ้าหน้าที่ยกเลิก) — API ให้ยืนยันใหม่ได้ (T3) แสดงสถานะ + ลิงก์แก้ไขด้านล่าง
          <p>
            <b className='color-orange'>{'ใบแจ้งชำระเงินถูกยกเลิก กรุณายืนยันการสมัครใหม่'}</b>
          </p>
        ) : null}
        <ul className='r-choose -confirmed'>
          {(recentAdmission.program_projects || []).map(({ _id, priority }) => {
            const project = courseMap[_id];
            const majorName = _.get(project, `major_name_th`);
            const projectName = _.get(project, `project_name_th`);
            const errors = notApplicable(profile, project, { studentStatus, favorites: [], nextPriority: priority });
            const admissionCountKey = `${_.get(project, 'program_id') || 0}_${_.get(project, 'major_id') || 0}_${_.get(project, 'project_id') || 0}`;
            return (
              <li key={priority} className={cn({ error: Boolean(errors.length) })}>
                <label style={{ pointerEvents: 'initial', ...(isMobile ? { paddingBottom: 60 } : {}) }}>
                  <div className='order'>
                    {'อันดับที่'} {priority}
                  </div>
                  <h4 className='score' style={isMobile ? { bottom: 38 } : {}}>
                    {'คะแนน'} {getProjectScore(profile, project, priority)}/{getFullScore(project)}
                    {!errors.length && (
                      <PassChance score={getProjectScore(profile, project, priority)} project={project} />
                    )}
                  </h4>
                  <h4
                    className='score'
                    style={isMobile
                      ? { right: 'auto', left: 'auto', bottom: 16, textAlign: 'left', pointerEvents: 'none' }
                      : { right: 16, left: 'auto', pointerEvents: 'none' }}
                  >
                    {`จำนวนผู้สมัคร ${(admissionCountMap[admissionCountKey] || 0).toLocaleString()} คน`}
                    {Number(_.get(project, 'last_year_register')) ? (
                      <span style={{ color: '#999' }}>{` (${PREV_TCAS_LABEL} : ${Number(_.get(project, 'last_year_register')).toLocaleString()} คน)`}</span>
                    ) : null}
                  </h4>
                  <span className='u-logo' style={{ backgroundImage: `url('/assets/img/logo/${_.get(project, 'university_id')}.png')` }} />
                  <strong>{_.get(project, `university_name_th`)}</strong>
                  {_.get(project, `campus_name_th`)}
                  <br />
                  {_.get(project, `faculty_name_th`)}
                  <br />
                  {_.get(project, `field_name_th`)}
                  <br />
                  {majorName && majorName !== '0' ? (
                    <>
                      {'วิชาเอก'}
                      {majorName}
                      <br />
                    </>
                  ) : null}
                  {_.get(project, `program_name_th`)}
                  <br />
                  {_.get(project, `program_type_name_th`) ? `(${_.get(project, `program_type_name_th`)})` : null}
                  {projectName && projectName !== '0' ? (
                    <>
                      <br />
                      {'โครงการ '}
                      {projectName}
                    </>
                  ) : null}
                </label>
                {Boolean(errors.length) && (
                  <div className={cn('t-collapse', { active: errorActive[_id] })}>
                    <div className='t-toggle' onClick={() => setErrorActive((_map) => ({ ..._map, [_id]: !_map[_id] }))}>
                      {'ไม่ผ่านเงื่อนไขการรับ ไม่สามารถสมัครได้'}
                      <i className='i-toggle i-down-red'></i>
                    </div>
                    <div className='t-content'>
                      <ul>
                        {errors.map(({ messages = [], key, label, expected, value }) => {
                          if (messages.length > 1) {
                            return (
                              <li key={key}>
                                <i className='i-no'></i>
                                <span>{messages[0].label}</span>
                                {messages.map(({ expected, value }, index) => (
                                  <div key={index}>
                                    <b>{expected}</b>
                                    <span>
                                      ({'ผู้สมัคร'}: <b className='color-red'>{value}</b>)
                                    </span>
                                  </div>
                                ))}
                              </li>
                            );
                          }
                          if (!value && value !== 0) {
                            return (
                              <li key={key}>
                                <i className='i-no'></i>
                                <span>{label}</span>
                              </li>
                            );
                          }
                          return (
                            <li key={key}>
                              <i className='i-no'></i>
                              <span>{label}</span>
                              <b>{expected}</b>
                              <span>
                                ({'ผู้สมัคร'}: <b className='color-red'>{value}</b>)
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className='t-summary'>
          <dl>
            <dt>{'จำนวนอันดับสาขาวิชา (เลือกได้สูงสุด 10 อันดับ)'}</dt>
            <dd>
              <b>{(recentAdmission.program_projects || []).length}</b> {'อันดับ'}
            </dd>
          </dl>
          {owedLateFee > 0 && (
            <dl data-testid='r3-late-fee'>
              <dt>
                <span>{'ค่าธรรมเนียมสมัครล่าช้า'}</span>
              </dt>
              <dd>
                <b>{owedLateFee.toFixed(2)}</b> {'บาท'}
              </dd>
            </dl>
          )}
          {(recentAdmission.sz_rollback || recentAdmission.rd_rollback || expiredInvoice)
            ? null
            : (!paidAmount && recentAdmission.order_id && recentAdmission.status !== 'canceled') ? (
              <dl>
                <dt>
                  <span className='color-red'>{'ยอดค้างชำระทั้งหมด'}</span>
                </dt>
                <dd className='color-red'>
                  <b>{(recentAdmission.total_amount - subsidyUsed({ alreadySetZero, skipRedemption, redeemableAmount, redeemedAmount }) + owedLateFee).toFixed(2)}</b> {'บาท'}
                </dd>
              </dl>
            ) : paidAmount < recentAdmission.total_amount ? (
              <>
                <dl>
                  <dt>
                    <span>
                      {'ยอดชำระเงิน'} ({'ชำระมาแล้ว'} {paidSubjectCount} {'อันดับ'})
                    </span>
                  </dt>
                  <dd>
                    <b>{(recentAdmission.program_projects || []).length - paidSubjectCount}</b> {'อันดับ'}
                  </dd>
                </dl>
                <dl>
                  <dt>
                    <span className='color-red'>
                      {'ยอดค้างชำระทั้งหมด'} ({'จ่ายเพิ่ม'} {recentAdmission.program_projects.length - (paidSubjectCount)} {'อันดับ'})
                    </span>
                  </dt>
                  <dd className='color-red'>
                    <b>{(recentAdmission.total_amount - paidAmount + owedLateFee).toFixed(2)}</b> {'บาท'}
                  </dd>
                </dl>
              </>
            ) : paidAmount >= recentAdmission.total_amount ? (
              <>
                {recentAdmission.confirm_id < 3 && paidSubjectCount > recentAdmission.program_projects.length && (
                  <dl>
                    <dt>
                      <span className='color-teal'>
                        {'สามารถเลือกได้อีก'} <b>{paidSubjectCount - recentAdmission.program_projects.length}</b> {'อันดับโดยไม่ต้องชำระเงินเพิ่ม'}
                      </span>
                    </dt>
                  </dl>
                )}
                {/* ใบล่าช้าที่ไม่มีค่าสาขาเพิ่ม (เช่น สลับอันดับ) ยังค้าง 200 */}
                <dl>
                  <dt>
                    <span className={owedLateFee ? 'color-red' : 'color-green'}>{'ยอดค้างชำระทั้งหมด'}</span>
                  </dt>
                  <dd className={owedLateFee ? 'color-red' : 'color-green'}>
                    <b>{owedLateFee.toFixed(2)}</b> {'บาท'}
                  </dd>
                </dl>
              </>
            ) : null}
        </div>
        {(recentAdmission.status === 'paid' || recentAdmission.rd_rollback || recentAdmission.sz_rollback) ? (
          <>
            <div className='t-note' style={{ textAlign: 'center' }}>
              {'ท่านทำการสมัครครั้งที่'} {recentAdmission.apply_id} / {'ยืนยันครั้งที่'} {recentAdmission.confirm_id} {`และชำระเงินเรียบร้อยแล้ว หลังจากนี้ให้รอประกาศผล วันที่ ${formatThaiShortDate(enabled.round3.reply_start)} และยืนยันสิทธิ์ วันที่ ${thaiMoment(enabled.round3.reply_start).utcOffset(420).format('D')}-${formatThaiShortDate(enabled.round3.reply_end)}`}
            </div>
            <p>
              <a className='btn-main -large cursor-pointer' onClick={onSummaryPdfOpen}>
                {'พิมพ์ใบสรุปการสมัคร'}
              </a>
            </p>
            <p>
              <a
                className='btn-main -large cursor-pointer'
                onClick={() => window.open('/profile/rank-check-round3?from=applied', '_blank')}
                style={{ backgroundImage: 'linear-gradient(to right, #1EBDE5, #2E6CB2)', backgroundSize: '100% 100%' }}
              >
                <img src='/assets/img/i/i-rank.svg' width='18' height='18' alt='' style={{ marginRight: 8, verticalAlign: 'middle' }} />
                {'เช็คเกณฑ์การจัดอันดับ'}
              </a>
            </p>
          </>
        ) : (recentAdmission.status === 'pending' && !expiredInvoice) ? (
          <div className='t-note'>
            {'สามารถชำระเงินผ่าน QR Code ทุกธนาคาร (ฟรี ไม่มีค่าธรรมเนียม) หรือชำระเงินผ่านเคาน์เตอร์เซอร์วิส (เซเว่น อีเลฟเว่น - ค่าธรรมเนียม 10 บาทต่อรายการ) ได้'}
            <p>
              <img src='/assets/img/logo/banks.png' width='328' />
              <img src='/assets/img/logo/cs-7-11.png' width='95' />
            </p>
            <p>{`เมื่อคลิกปุ่มพิมพ์ใบสมัคร ระบบจะสร้างใบแจ้งชำระเงินในรูปแบบ PDF ให้ท่านพิมพ์หรือจับภาพหน้าจอเพื่อนำไปชำระเงิน โดยให้ชำระเงินภายในเวลา 23.29 น. ของวันที่ ${formatThaiShortDate(paymentDeadline)}`}</p>
            <p>{'หมายเหตุ: การสมัครนี้จะสมบูรณ์ เมื่อท่านกดพิมพ์ใบแจ้งชำระเงิน และชำระเงินในระบบครบถ้วนแล้ว'}</p>
            <h4 className={cn('h-status color-red')}>
              {'สถานะ'}: {statusMap[recentAdmission.status]}
            </h4>
            <p>
              <a className={cn('btn-main -large cursor-pointer', { loading: invoiceOpening })} onClick={onInvoicePdfOpen}>
                {'พิมพ์ใบแจ้งชำระเงิน'} {(recentAdmission.payment_amount || 0) + lateFee} บาท
              </a>
            </p>
            <h4>
              <a className='cursor-pointer' onClick={() => window.location.reload()}>
                <i className='i-reload' />
                {'คลิกเพื่อตรวจสอบสถานะล่าสุด'}
              </a>
            </h4>
            {recentPaidAdmission.confirm_id && (
              <>
                <br />
                <h4 className='h-status color-red'>
                  {'หากไม่มีการชำระเงิน ระบบจะถือว่าท่านยังยืนยัน'}{' '}
                  <a onClick={onSummaryPdfOpen} className='cursor-pointer color-red'>
                    <u>
                      {'การสมัครครั้งที่'} {recentPaidAdmission.apply_id} / {'ยืนยันครั้งที่'} {recentPaidAdmission.confirm_id}
                    </u>
                  </a>
                </h4>
              </>
            )}
          </div>
        ) : (expiredInvoice && recentPaidAdmission.confirm_id) ? (
          <h4 className='h-status color-red'>
            {'หากไม่ยืนยันใหม่ ระบบจะถือว่าท่านยังยืนยัน'}{' '}
            <a onClick={onSummaryPdfOpen} className='cursor-pointer color-red'>
              <u>
                {'การสมัครครั้งที่'} {recentPaidAdmission.apply_id} / {'ยืนยันครั้งที่'} {recentPaidAdmission.confirm_id}
              </u>
            </a>
          </h4>
        ) : null}
        {(isExpired || !(['paid', 'canceled'].includes(recentAdmission.status) || expiredInvoice)) ? null
          // R27-2: ใบถูกยกเลิกเพราะหลักสูตรถูกถอน — ยืนยันใหม่ได้แม้ครบ 3 ครั้ง (API อนุญาต) ไม่ต้อง Set Zero
          // ช่วงล่าช้า: ใบครั้งที่ 3 หมดอายุ = แทนครั้งที่ 3 ได้เช่นกัน (BE replacesWithdrawn)
          : (recentAdmission.confirm_id < 3 || replacesThirdConfirmation(recentAdmission, phase)) ? (
          <div className={cn('t-collapse', { active: editActive })} style={{ borderBottom: 'none' }}>
            <a className='t-toggle color-orange _heading' onClick={() => setEditActive((_active) => !_active)}>
              {'ต้องการแก้ไขสาขาวิชาที่สมัคร'}?
              <i className='i-toggle i-down-red' style={{ width: 14, height: 16 }} />
            </a>
            <div className='t-content'>
              <h4>
                <b>
                  {'ท่านตัดสินใจไปแล้ว'} {quotaUsed} {'ครั้ง'}
                </b>
              </h4>
              <div className='i-hearts'>
                <i className={cn('i-heart', { '-blink': quotaUsed === 2, '-used': quotaUsed > 2 })} />
                <i className={cn('i-heart', { '-blink': quotaUsed === 1, '-used': quotaUsed > 1 })} />
                <i className={cn('i-heart', { '-blink': quotaUsed === 0, '-used': quotaUsed > 0 })} />
                <small>
                  {'สามารถตัดสินใจได้อีก'} {3 - quotaUsed} {'ครั้ง'}
                </small>
              </div>
              {phase === 'late' && (
                <p className='color-orange' data-testid='r3-late-edit-note'>
                  {`ช่วงสมัครล่าช้า: ไม่สามารถสลับหรือลดอันดับเดิมได้ ยืนยันได้เมื่อเพิ่มสาขาวิชาใหม่และคงสาขาวิชาเดิมไว้ครบ (สลับลำดับได้เมื่อเพิ่มสาขา) การยืนยันการสมัครทุกครั้งมีค่าธรรมเนียมสมัครล่าช้า ${LATE_FEE} บาท`}
                </p>
              )}
              <a className='btn-ghost cursor-pointer' onClick={onEdit}>
                {'ดำเนินการแก้ไขสาขาวิชาที่สมัคร'} →
              </a>
            </div>
          </div>
        ) : phase === 'late' ? (
          // ทปอ. 3 พ.ย. 69: ยกเลิกการสมัครเดิมแล้วสมัครใหม่ในช่วงล่าช้าไม่ได้ (BE set-zero = ช่วงปกติเท่านั้น)
          <p className='color-orange' data-testid='r3-late-no-set-zero'>{'ช่วงสมัครล่าช้าไม่สามารถดำเนินการสมัครใหม่ (Set Zero) ได้'}</p>
        ) : (
          <Link to='/profile/set-zero' className='btn-underline cursor-pointer'>
            {'ดำเนินการสมัครใหม่'} (Set Zero)
          </Link>
        )}
      </div>
    </div>
  );
};

export default AdmissionApplied;
